import React, { useEffect, useMemo, useState } from "react";
import Dialog from "../../CoreComponent/Dialog";
import ImageUpload from "../../CoreComponent/ImageUpload";
import "./style.scss";
import toast from "react-hot-toast";
import axios from "axios";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const ReservationsFiles = () => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const openMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleUploadClick = (row) => {
    setSelectedUser(row);
    setSelectedId(row?.id ?? null);
    setDialogOpen(true);
    closeMenu();
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setSelectedId(null);
    setFile(null);
  };

  const getData = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("Token is missing or expired.");
      toast.error("Token is missing or expired.");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(`${BaseUrl}/reservation/approved`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setData(response?.data?.invoices ?? []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch reservation files.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("Token is missing or expired.");
      toast.error("Token is missing or expired.");
      return;
    }

    if (!selectedId) {
      toast.error("Reservation id is missing.");
      return;
    }

    if (!file) {
      console.error("No file selected.");
      toast.error("Please select a file.");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("confirmationPDF", file);

      await axios.post(`${BaseUrl}/add/confirmation/${selectedId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("File uploaded successfully.");
      closeDialog();
      getData();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const rows = useMemo(() => {
    return (data ?? []).map((invoice, index) => ({
      id: invoice?.id ?? `row-${index}`,
      email: invoice?.room?.reservation?.user?.email ?? "-",
      name: invoice?.room?.occupant_name ?? "-",
      user_type: invoice?.room?.user_type ?? "-",
      status: invoice?.status ?? "-",
      confirmationPDF: invoice?.confirmationPDF ?? null,
      rawData: invoice ?? null,
    }));
  }, [data]);

  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "name",
      headerName: "User Name",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "user_type",
      headerName: "User Type",
      flex: 1,
      minWidth: 180,
      cellClassName: "centered-cell",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      cellClassName: "centered-cell",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      cellClassName: "centered-cell",
      renderCell: (params) => (
        <>
          <IconButton onClick={(event) => openMenu(event, params.row)}>
            <MoreVertIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow?.id === params.row.id}
            onClose={closeMenu}
          >
            <MenuItem onClick={() => handleUploadClick(params.row)}>
              Upload Confirmation File
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <div className="flights-files-container">
      <h1 className="visa-files" style={{ color: "#9B1321" }}>
        Reservations Files
      </h1>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        getRowHeight={() => "auto"}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5 },
          },
        }}
        pageSizeOptions={[5, 10, 20]}
        checkboxSelection
        disableRowSelectionOnClick
        autoHeight
        sx={{
          marginTop: "20px",
          "& .MuiDataGrid-virtualScroller": {
            overflow: "hidden",
          },
        }}
      />

      {isDialogOpen && (
        <Dialog
          viewHeader={true}
          header="Upload Hotel Booking Confirmation File"
          open={isDialogOpen}
          setOpen={setDialogOpen}
        >
          <div className="dialog-content-files">
            <div className="file-con">
              <ImageUpload
                required
                label="Confirmation File"
                allowedExtensions={["pdf", "jpg", "jpeg", "png"]}
                inputValue={file}
                setInputValue={setFile}
                className="image-upload"
                placeholder="Choose a file"
              />
            </div>

            <div className="buttons-file">
              <button
                className="save-button"
                onClick={handleSubmit}
                disabled={uploading}
              >
                {uploading ? "Saving..." : "Save"}
              </button>

              <button className="cancel-button" onClick={closeDialog}>
                Cancel
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default ReservationsFiles;